import {
  addEventParam,
  INinjaEvent,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import { KissanEventType, useEdTechProductStore, useProfileStore } from '@ninjacart/nc-nk-store';
import { Container, KisanLogoNew, Paragraph, Text, useTheme, View, XStack } from '@ninjacart/nc-nk-ui';
import { useNavigation, useRouter } from '@ninjacart/nc-nk-web-deps';
import { ChevronLeft } from '@tamagui/lucide-icons';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

interface IEdTechHomeHeaderProps {
  title?: string;
  kisanLogoShow?: boolean;
  showMyOrder?: boolean;
  navigateToMyCourses?: () => void;
  applyMargin?: boolean;
  paymentSuccess?: boolean;
}

const EdTechHomeHeader = ({
  title,
  kisanLogoShow,
  showMyOrder,
  navigateToMyCourses,
  applyMargin = false,
  paymentSuccess = false,
}: IEdTechHomeHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { back, push } = useRouter();
  const navigation = useNavigation();
  const cart = useEdTechProductStore((state) => state.cart);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const userProfile = useProfileStore((state) => state?.userProfile);
  const deviceId = useProfileStore((state) => state.deviceId);
  const screeName = useEdTechProductStore((state) => state.screenName);
  const coursesToView = useEdTechProductStore((state) => state.coursesToView);

  const navigateToPayment = () => {
    handleVariantChangeEvent();
    push('/edTech/myCart/myCartList');
  };

  const handleVariantChangeEvent = () => {
    const productInterestEvent: INinjaEvent = {
      name: KissanEventType.PRODUCT_CART_CLICK,
      screenDetails: { screen: screeName ?? '' },
      eventParams: [addEventParam('cartItem', JSON.stringify(cart) ?? '')],
    };
    enqueue(productInterestEvent);
  };

  const onGoBack = () => {
    if (paymentSuccess) {
      navigation?.reset({
        index: 0,
        routes: [{ name: '(tabs)' }],
      });
      return;
    }

    if (navigation) {
      if (navigation?.canGoBack()) {
        console.log('Nav go back!!!');
        navigation?.goBack();
        return;
      }
      navigation?.reset({
        index: 0,
        routes: [{ name: '(tabs)' }],
      });
    } else {
      back();
      return;
    }
  };

  const navigateToMyCoursesWithoutEvent = () => {
    push('/edTech/course/myCourse');
  };

  return (
    <Container>
      <View
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor="$background"
        paddingBottom={16}
      >
        <XStack marginTop={!applyMargin ? 10 : 0} alignItems={'center'}>
          {kisanLogoShow ? (
            <XStack>
              <KisanLogoNew width={130} height={48} />
            </XStack>
          ) : (
            <>
              <XStack
                flexDirection={'row'}
                borderRadius={16}
                borderWidth={1}
                borderColor={'$grey'}
                background={theme['background']?.val}
                padding={6}
                marginRight={5}
                alignItems={'center'}
                marginTop={10}
              >
                <ChevronLeft
                  color={'$weatherTextColor'}
                  size="$2"
                  onPress={onGoBack}
                  testID="kisan_tId_header_backButton"
                />
              </XStack>
              <Paragraph
                paddingHorizontal={10}
                fontSize={21}
                fontWeight={'600'}
                color={'$color'}
                paddingTop={10}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {title}
              </Paragraph>
            </>
          )}
        </XStack>
        <XStack alignItems={'center'} paddingTop={10}>
          {showMyOrder && (
            <Pressable
              onPress={navigateToMyCourses ?? navigateToMyCoursesWithoutEvent}
              testID="kisan_tId_header_myOrdersButton"
            >
              <XStack
                borderRadius={10}
                borderWidth={1}
                borderColor="$grey"
                backgroundColor="$background"
                padding={10}
                marginTop={10}
                marginRight={10}
              >
                <Text fontSize={14} color="$primary">
                  {t('page.myOrders')}
                </Text>
                {coursesToView?.length > 0 && (
                  <View
                    position={'absolute'}
                    top={-5}
                    right={-5}
                    backgroundColor="$primary"
                    borderRadius={10}
                    width={20}
                    height={20}
                    justifyContent="center"
                    alignItems="center"
                    zIndex={1}
                  >
                    <Text color="$light" fontSize={12} fontWeight={600}>
                      {coursesToView?.length}
                    </Text>
                  </View>
                )}
              </XStack>
            </Pressable>
          )}
        </XStack>
      </View>
    </Container>
  );
};

export default EdTechHomeHeader;
